/**
 * Theme Store
 *
 * Manages the application theme preference.
 * Persists to localStorage and applies theme class to document.
 * Tracks theme changes via PostHog analytics.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { THEMES, DEFAULT_THEME, THEME_STORAGE_KEY } from '../config/themes';
import { analyticsService } from '../services/analyticsService';

export type ThemeId = string;

interface ThemeStore {
  /** Current theme ID */
  theme: ThemeId;

  /** Set the theme (applies to DOM and tracks analytics) */
  setTheme: (theme: ThemeId, previousTheme?: ThemeId) => void;

  /** Initialize theme on app load (applies saved theme to DOM) */
  initializeTheme: () => void;
}

/** Apply theme class to document element */
function applyThemeToDOM(themeId: ThemeId): void {
  const root = document.documentElement;

  // Remove all theme classes
  THEMES.forEach((theme) => {
    root.classList.remove(`theme-${theme.id}`);
  });

  // Add current theme class
  root.classList.add(`theme-${themeId}`);
}

export const useThemeStore = create<ThemeStore>()(
  devtools(
    persist(
      (set, get) => ({
        theme: DEFAULT_THEME,

        setTheme: (theme, previousTheme) => {
          const prev = previousTheme ?? get().theme;

          // Apply to DOM
          applyThemeToDOM(theme);

          // Track analytics
          if (prev !== theme) {
            analyticsService.trackEvent('theme_changed', {
              theme,
              previousTheme: prev,
            });
          }

          set({ theme }, false, `setTheme:${theme}`);
        },

        initializeTheme: () => {
          const { theme } = get();
          applyThemeToDOM(theme);
        },
      }),
      {
        name: THEME_STORAGE_KEY,
        partialize: (state) => ({ theme: state.theme }),
        onRehydrate: () => {
          return (state) => {
            // Apply theme to DOM after rehydration
            if (state) {
              applyThemeToDOM(state.theme);
            }
          };
        },
      }
    ),
    {
      name: 'theme-store',
    }
  )
);

// Selectors
export const selectTheme = (state: ThemeStore) => state.theme;
